import React, { useEffect, useState } from 'react';
import { useStoreActions } from 'easy-peasy';
import {
  checkUserType,
  initMenuVisibility,
  initProfileButtons,
} from '../utils/userUtil';
import { useSnackBar } from './snackBarHooks';
import { getCurrentUser, getPostLanguage } from '../services/authService';
import language from '../utils/languages';
import {
  AccountCircle,
  AddBox,
  Block,
  CloseSharp,
  IndeterminateCheckBox,
  Share,
} from '@material-ui/icons';
import BlockUserIcon from '../img/block-user.png';
import UnFriendIcon from '../img/unfriend-icon.png';
import DeactivateIcon from '../img/deactivate.png';
import userService from '../services/userService';
import pollService from '../services/pollService';
import profileService from '../services/profileService';
import { Box, Button, Typography, makeStyles } from '@material-ui/core';
import AddFriendIcon from '../img/addFriend.png';
import { eventNames, eventProps } from '../utils/eventConstants';
import analytics from '../services/analyticsService';
import { userType } from '../utils/constants';

function useGetUserData(userId) {
  const [data, setData] = useState([]);
  const { fetchUserData } = useStoreActions((actions) => actions.feed);
  useEffect(() => {
    async function getData() {
      const response = await fetchUserData({
        userId,
      });
      if (response) {
        setData(response);
      }
    }
    getData();
    // eslint-disable-next-line
  }, [userId]);
  return data && data;
}

/**
 * Author: Shubh Kirti
 * Date: 21 Sep, 2023
 * @param {boolean} isUserProfile
 * @param {boolean} isFeed
 * @param {object} userInfoObject
 * @param {number} userId
 * @param {Function} setShowBtn
 * @param {number} followersCount
 * @param {Function} setFollowersCount
 * @param {Function} setIsBlockedUser
 * @param {number} friendsCount
 * @param {Function} setFriendsCount
 * @return {*} This hook will return profile, feed, followers, following list buttons of follow, following, add friend, pending, confirm and also handling the user-menu (3 dots) functionality.
 */
function useProfileButtons({
  isUserProfile,
  isFeed,
  userInfoObject,
  userId,
  setShowBtn,
  followersCount,
  setFollowersCount,
  setIsBlockedUser,
  friendsCount,
  setFriendsCount,
  showBtn,
  isCarousel,
}) {
  const webLanguage = getPostLanguage();
  const languageText = language[webLanguage ? webLanguage : 'en'];
  const [visibility, setVisibility] = useState(initMenuVisibility);
  const { showCancelRequest, showUnFriend } = visibility;
  const { isFollowed, isFriend, name, id, participantId } = userInfoObject;
  const currentUser = getCurrentUser();
  const setToastMessage = useSnackBar('');
  const useStyles = makeStyles((theme) => ({
    heightOfTextBox: {
      minHeight: theme.spacing(10),
    },
    followers: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      cursor: 'pointer',
    },
    shareBtn: {
      height: '35px',
      padding: theme.spacing(0),
      width: '100%',
    },
    carouselBtn: {
      fontSize: '12px',
      minWidth: '120px',
      borderRadius: '24px',
      border: '1px solid #DC4541',
      padding: '6px 8px',
      color: '#DC4541',
      cursor: 'pointer',
    },
    carouselText: {
      textAlign: 'center',
      cursor: 'text',
      minWidth: '120px',
      fontSize: '12px',
    },
    btn: ({ isFeed, isUserProfile }) =>
      isFeed
        ? {
            fontSize: '12px',
            padding: '0.5px 9px',
            borderRadius: '12px',
            color: '#DC4541',
            border: '1px solid #DC4541',
            cursor: 'pointer',
          }
        : isUserProfile
        ? {}
        : {
            display: 'flex',
            color: '#DC4541',
            border: '1px solid #DC4541',
            width: theme.spacing(33),
            borderRadius: '16px',
            alignItems: 'center',
            marginBottom: '10px',
            justifyContent: 'center',
            textDecoration: 'none',
            cursor: 'pointer',
            marginTop: theme.spacing(2),
          },
  }));
  const classes = useStyles({ isFeed, isUserProfile });
  const selfProfile =
    (currentUser && currentUser.id && currentUser.id) === participantId;

  // OnClick-Functionalities-Section
  async function userFollowers(toFollow) {
    const { pending, addFriend } = showBtn;
    const responseFromFollowers = await profileService.followProfile(
      participantId,
      toFollow,
      setToastMessage
    );
    if (responseFromFollowers && responseFromFollowers.status === 'SUCCESS') {
      toFollow
        ? setShowBtn({
            ...initProfileButtons,
            following: pending || addFriend ? false : true,
            pending: pending ? true : false,
            addFriend: addFriend ? true : false,
          })
        : setShowBtn({
            ...initProfileButtons,
            follow: pending || addFriend ? false : true,
            pending: pending ? true : false,
            addFriend: addFriend ? true : false,
          });

      if (toFollow) {
        setFollowersCount && setFollowersCount(followersCount + 1);
        setVisibility({
          ...initMenuVisibility,
          showUnFollow: true,
          showUnFriend: showUnFriend,
          showCancelRequest: showCancelRequest ? true : false,
        });
      } else {
        setFollowersCount && setFollowersCount(followersCount - 1);
        setVisibility({
          ...initMenuVisibility,
          showFollow: true,
          showUnFriend: showUnFriend,
          showCancelRequest: showCancelRequest ? true : false,
        });
      }

      const eventName = toFollow
        ? eventNames.profileFollowed
        : eventNames.profileUnFollowed;

      analytics.track(eventName, {
        [eventProps.followedUserId]: id,
        [eventProps.name]: name,
        [eventProps.type]: userInfoObject && userInfoObject.userType,
      });
      return responseFromFollowers;
    }
  } // After Clicking Follow Button, Button text should be Following and vice-versa.

  async function friendRequestAccept() {
    try {
      const response = await profileService.getConfirmFriend(
        participantId,
        userInfoObject && userInfoObject.receivedFriendRequestId,
        setToastMessage
      );
      if (
        (response && response.status && response.status === 'SUCCESS') ||
        (userInfoObject && userInfoObject.isFriend === true)
      ) {
        isUserProfile
          ? setShowBtn({ ...initProfileButtons, following: true })
          : setShowBtn({ ...initProfileButtons, sayHi: true });
        setVisibility({ ...initMenuVisibility, showCancelRequest: false });
        setToastMessage(
          name
            ? `Yay! You are friends now with ${name}.`
            : 'Yay! You are friends now'
        );
      }

      return response;
    } catch (err) {
      return err;
    }
  } // After Clicking Confirm Button, Button text should be Say Hi. hence now you are friends.

  async function friendRequestSend() {
    try {
      const data = await profileService.getAddFriend(
        userId,
        participantId,
        isFeed,
        setToastMessage
      );
      if (
        (data && data.status === 'SUCCESS') ||
        (userInfoObject && userInfoObject.isRequestSent === true)
      ) {
        setShowBtn({ ...initProfileButtons, pending: true });
        setVisibility({
          ...initMenuVisibility,
          showCancelRequest: true,
          showUnFollow: isFollowed ? true : false,
          showFollow: isFollowed ? false : true,
        });
      }
      return data;
    } catch (error) {
      return error;
    }
  } // After Clicking Add Friend Button text should change to Pending.hence considered that friend request sent successfully.

  // Sub-Buttons-UI-Section
  const sayHiBtn = () => {
    return isUserProfile
      ? selfProfile && (
          <Button
            variant="outlined"
            color="primary"
            className={classes.shareBtn}
          >
            {languageText.FOLLOWING}
          </Button>
        )
      : !selfProfile && (
          <Box className={classes.heightOfTextBox}>
            <Box className={classes.btn}>{languageText.SAY_HI}</Box>
          </Box>
        );
  };

  function filledGreyPending() {
    return (
      isUserProfile && (
        <Button
          variant="contained"
          style={{
            cursor: 'text',
            backgroundColor: '#e5e2e4',
            color: 'grey',
            boxShadow: 'none',
          }}
          className={classes.shareBtn}
        >
          {languageText.PENDING}
        </Button>
      )
    );
  }

  function pendingText() {
    return (
      !selfProfile && (
        <Box className={classes.heightOfTextBox}>
          <Box
            style={{ marginTop: isFeed ? '-0.7px' : '5px', cursor: 'text' }}
            className={isCarousel ? classes.carouselText : classes.followers}
          >
            <Typography
              color="textSecondary"
              variant={isFeed ? 'body2' : 'subtitle2'}
            >
              {languageText.PENDING}
            </Typography>
          </Box>
        </Box>
      )
    );
  }

  function redFollowingBtn() {
    return (
      isUserProfile && (
        <Button
          variant="outlined"
          color={'primary'}
          className={classes.shareBtn}
        >
          {languageText.FOLLOWING}
        </Button>
      )
    );
  }

  function greyFollowingBtn() {
    return (
      <Button
        onClick={() => userFollowers(false)}
        variant="outlined"
        color={'secondary'}
        className={classes.shareBtn}
      >
        {languageText.FOLLOWING}
      </Button>
    );
  }

  function greyFollowingText() {
    return (
      <Box className={classes.heightOfTextBox}>
        {!selfProfile && (
          <Box
            style={{ marginTop: isFeed ? '-0.7px' : '5px' }}
            className={isCarousel ? classes.carouselText : classes.followers}
            onClick={() => userFollowers(false)}
          >
            <Typography
              variant={isFeed ? 'body2' : 'subtitle2'}
              color="textSecondary"
            >
              {languageText.FOLLOWING}
            </Typography>
          </Box>
        )}
      </Box>
    );
  }
  function addFriendWithIcon() {
    return (
      <Button
        variant="outlined"
        color="primary"
        className={classes.shareBtn}
        onClick={friendRequestSend}
      >
        {isUserProfile && (
          <img src={AddFriendIcon} width="25" height="25" alt="" />
        )}
        {languageText.ADD_FRIEND}
      </Button>
    );
  }

  function addFriendOutlinedBtn() {
    return (
      <Box className={isCarousel ? '' : classes.heightOfTextBox}>
        {!selfProfile && (
          <Box
            className={isCarousel ? classes.carouselBtn : classes.btn}
            onClick={friendRequestSend}
          >
            {languageText.ADD_FRIEND}
          </Box>
        )}
      </Box>
    );
  }

  function filledConfirmBtn() {
    return (
      isUserProfile && (
        <Button
          onClick={friendRequestAccept}
          variant={'contained'}
          color="primary"
          style={{ boxShadow: 'none' }}
          className={classes.shareBtn}
        >
          {languageText.CONFIRM}
        </Button>
      )
    );
  }
  function outlinedConfirmBtn() {
    return (
      isUserProfile && (
        <Button
          onClick={friendRequestAccept}
          variant="outlined"
          color="primary"
          style={{ boxShadow: 'none' }}
          className={classes.shareBtn}
        >
          {languageText.CONFIRM}
        </Button>
      )
    );
  }

  function fixedHeightConfirmBtn() {
    return (
      <Box
        className={isCarousel ? classes.carouselBtn : classes.heightOfTextBox}
      >
        {!selfProfile && (
          <Box className={classes.btn} onClick={friendRequestAccept}>
            {languageText.CONFIRM}
          </Box>
        )}
      </Box>
    );
  }

  function redOutlinedFollowBtn() {
    return isUserProfile ? (
      <Button
        onClick={() => userFollowers(isFriend ? false : true)}
        variant="outlined"
        color="primary"
        className={classes.shareBtn}
      >
        {languageText.FOLLOW}
      </Button>
    ) : (
      // This UI is for follow or following or champion list page.
      <Box
        className={isCarousel ? classes.carouselBtn : classes.btn}
        onClick={() => userFollowers(isFriend ? false : true)}
      >
        {languageText.FOLLOW}
      </Box>
    );
  }

  function redFollowText() {
    return (
      !selfProfile && (
        <Typography
          className={isFollowed && isFriend ? ' ' : classes.btn}
          onClick={() => userFollowers(isFollowed && isFriend ? false : true)}
        >
          {languageText.FOLLOW}
        </Typography>
      )
    );
  }

  // Buttons-Rendering-Section
  function followingBtn() {
    return isUserProfile
      ? isFriend
        ? redFollowingBtn()
        : greyFollowingBtn()
      : greyFollowingText();
  }

  function followBtn() {
    return isUserProfile
      ? redOutlinedFollowBtn()
      : isFeed
      ? redFollowText()
      : redOutlinedFollowBtn();
  }

  function confirmTextBtn() {
    return isUserProfile
      ? filledConfirmBtn()
      : isFeed
      ? outlinedConfirmBtn()
      : fixedHeightConfirmBtn();
  }

  function pendingBtn() {
    return isUserProfile ? filledGreyPending() : pendingText();
  }
  function addFriendBtn() {
    return isUserProfile ? addFriendWithIcon() : addFriendOutlinedBtn();
  }

  // User-Menu-section or 3-dots functionalities
  const deactivateAccess =
    (userInfoObject && userInfoObject.postType === 'U') ||
    checkUserType(userType.isAdmin) ||
    checkUserType(userType.isCommunityMod);

  const reactivateAccess =
    (userInfoObject && userInfoObject.postType === 'U') ||
    checkUserType(userType.isAdmin) ||
    checkUserType(userType.isCommunityMod);

  const { updateSpamState } = useStoreActions((actions) => actions.reportSpam);
  const { updateUserDeactivateState } = useStoreActions(
    (actions) => actions.user
  );
  const { setDialogBoxOpen, setDialogContent } = useStoreActions(
    (actions) => actions.dialogBox
  );
  const shareAccess =
    currentUser &&
    currentUser.userTypeId &&
    (currentUser.userTypeId === 2 || currentUser.userType === 'Admin');
  const shareUserInfo = {
    text: '',
    url:
      userInfoObject &&
      (userInfoObject.shortBranchUrl || userInfoObject.sheroesDeeplink),
    title: 'SHEROES',
  };
  const optionsMenu = [
    {
      id: 'cancelRequest',
      name: languageText.CANCEL_REQUEST,
      access: visibility && visibility.showCancelRequest,
      icon: () => {
        return <CloseSharp className="userMenuIcon" />;
      },
    },

    {
      id: 'unFriend',
      name: languageText.UNFRIEND,
      access: visibility && visibility.showUnFriend,
      icon: () => {
        return <img src={UnFriendIcon} alt="" className="userMenuIcon" />;
      },
    },
    {
      id: 'unFollow',
      name: languageText.UNFOLLOW,
      access: visibility && visibility.showUnFollow,
      icon: () => {
        return <IndeterminateCheckBox className="userMenuIcon" />;
      },
    },
    {
      id: 'followUser',
      name: languageText.FOLLOW,
      access: visibility && visibility.showFollow,
      icon: () => {
        return <AddBox className="userMenuIcon" />;
      },
    },
    {
      id: 'reportUser',
      name: languageText.REPORT_USER,
      access: true,
      icon: () => {
        return <Block className="userMenuIcon" />;
      },
    },
    {
      id: 'blockedUser',
      name: languageText.BLOCK_USER,
      access: true,
      icon: () => {
        return <img src={BlockUserIcon} alt="" className="userMenuIcon" />;
      },
    },
    {
      id:
        userInfoObject && userInfoObject.isActive ? 'deactivate' : 'reactivate',
      name:
        userInfoObject && userInfoObject.isActive
          ? 'Deactivate User'
          : 'Reactivate User',
      access:
        userInfoObject && userInfoObject.isActive
          ? deactivateAccess
          : reactivateAccess,
      icon:
        userInfoObject && userInfoObject.isActive
          ? () => {
              return (
                <img src={DeactivateIcon} alt="" className="userMenuIcon" />
              );
            }
          : () => {
              return (
                <AccountCircle color="secondary" className="userMenuIcon" />
              );
            },
    },
    {
      id: 'share',
      name: languageText.SHARE_POST,
      access: shareAccess,
      shareData: shareUserInfo,
      icon: () => {
        return <Share className="userMenuIcon" />;
      },
    },
  ];

  function handleDeactivate() {
    updateUserDeactivateState({
      showDeactivateModal: true,
    });
  }

  async function handleReactivate() {
    const data = await userService.userReactivate(userId, setToastMessage);
    if (data && data.status === 'SUCCESS') {
      setToastMessage('Success! the user has been Reactivated.');
      window.location.reload();
    }
  }
  function handleBlockUser() {
    setDialogBoxOpen(true);
    setDialogContent({
      dialogTitle: languageText.WARNING,
      dialogContentText: languageText.ARE_YOU_WANT_TO_BLOCK_USER,
      btnPrimaryText: languageText.CANCEL,
      btnSecondaryText: languageText.BLOCK,
      btnSecondaryAction: blockUser,
    });
  }
  async function blockUser() {
    try {
      await pollService.toggleBlockedUser(userId, true, setToastMessage);
      setIsBlockedUser && setIsBlockedUser(true);
      setDialogBoxOpen(false);
      setToastMessage('You successfully blocked the user.');
    } catch (error) {
      setToastMessage(languageText.PLEASE_TRY_AGAIN_AFTER_SOMETIME);
    }
  }
  function handleReportUser() {
    const docInfo = {
      docId: userInfoObject && userInfoObject.participantId,
      communityId: 0,
      docAuthorId: userId,
    };
    updateSpamState({
      showSpamModal: true,
      contentType: 'user',
      docInfo,
    });
  }

  async function handleCancelRequest() {
    try {
      const data = await profileService.cancelFriendRequest(
        userInfoObject && userInfoObject.participantId,
        setToastMessage
      );
      if (data && data.status === 'SUCCESS') {
        if (isFriend) {
          setVisibility({
            ...initMenuVisibility,
            showUnFollow: isFollowed,
            showFollow: !isFollowed,
          });
          setShowBtn({ ...initProfileButtons, addFriend: true });
        } else {
          setVisibility({
            ...initMenuVisibility,
            showUnFollow: isFollowed,
            showFollow: !isFollowed,
          });
          setShowBtn({ ...initProfileButtons, addFriend: true });
        }
      }
    } catch {
      setToastMessage(languageText.PLEASE_TRY_AGAIN_AFTER_SOMETIME);
    }
  }

  async function handleUnFriend() {
    try {
      const data = await profileService.unFriendRequest(
        userInfoObject && userInfoObject.participantId,
        setToastMessage
      );
      if (data && data.status === 'SUCCESS') {
        isFriend && setFriendsCount && setFriendsCount(friendsCount - 1);
        setVisibility({
          ...initMenuVisibility,
          showUnFollow: isFollowed ? true : false,
          showFollow: isFollowed ? false : true,
          showUnFriend: false,
        });
        setShowBtn({ ...initProfileButtons, addFriend: true });
      }
    } catch {
      setToastMessage(languageText.PLEASE_TRY_AGAIN_AFTER_SOMETIME);
    }
  }

  async function handleUnFollow() {
    try {
      await userFollowers(false);
      if (isFollowed) {
        setVisibility({
          ...initMenuVisibility,
          showFollow: true,
          showCancelRequest: showCancelRequest ? true : false,
          showUnFriend: showUnFriend,
        });
      }
    } catch (err) {
      setToastMessage(
        (err && err.error_message && err.error_message) ||
          languageText.PLEASE_TRY_AGAIN_AFTER_SOMETIME
      );
    }
  }
  async function handleFollowUser() {
    try {
      await userFollowers(true);
      if (!isFollowed) {
        setVisibility({
          ...initMenuVisibility,
          showUnFollow: true,
          showCancelRequest: showCancelRequest ? true : false,
          showUnFriend: showUnFriend,
        });
      }
    } catch (err) {
      setToastMessage(
        (err && err.error_message && err.error_message) ||
          languageText.PLEASE_TRY_AGAIN_AFTER_SOMETIME
      );
    }
  }

  return {
    sayHiBtn,
    pendingBtn,
    followingBtn,
    followBtn,
    confirmTextBtn,
    addFriendBtn,
    userFollowers,
    handleDeactivate,
    handleReactivate,
    handleBlockUser,
    handleReportUser,
    handleCancelRequest,
    handleUnFriend,
    handleUnFollow,
    handleFollowUser,
    optionsMenu,
    visibility,
    setVisibility,
  };
}
export { useGetUserData, useProfileButtons };
